import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button, Input, Tag, Empty, Tooltip } from 'antd'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { stripTransientProps } from 'src/utils/stripTransientProps'
import moment from 'moment'
import {
  formatInvoiceTotalDisplay,
  getInvoiceMatchingDisplayLabel,
} from 'src/utils/invoiceUploadHelpers'
import {
  canLinkAndReconcileSelection,
  getLinkExistingConfirmState,
  getLinkExistingEmptyState,
  getLinkExistingInvoiceId,
  getLinkExistingListsForOrder,
  GR_LINK_EXISTING_COPY,
} from 'src/utils/goodsReceiptingLinkExistingHelpers'
import {
  CN_DOCUMENT_COPY,
  isCreditNoteDocument,
} from 'src/utils/creditNoteDocumentHelpers'
import { buildInvoicesPath } from 'src/utils/reconciliationHealthApi'
import { getGrActionToneStyles } from 'src/utils/goodsReceiptingActionHelpers'

const { Search } = Input

/**
 * Link an open library invoice to this GR PO (same link / one-click rules as Match).
 */
const GrLinkExistingInvoiceModal = ({
  visible,
  venueId,
  order,
  invoiceRows = [],
  onCancel,
  onBack,
  onUploadNew,
  selectedInvoiceId = null,
  onSelectInvoice,
  onLinkOnly,
  onLinkAndReconcile,
  confirmLoading = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    if (!visible) return
    setSearchQuery('')
  }, [visible, order?.purchaseOrderNumber])

  const lists = useMemo(
    () => getLinkExistingListsForOrder(order, invoiceRows, searchQuery),
    [order, invoiceRows, searchQuery],
  )

  if (!order) return null

  const po = String(order.purchaseOrderNumber || '').trim()
  const supplierTitle = String(order.supplier?.title || '').trim()
  const queryActive = Boolean(String(searchQuery || '').trim())
  const selected = (lists.candidates || []).find(
    invoice =>
      getLinkExistingInvoiceId(invoice) === String(selectedInvoiceId || ''),
  )
  const confirmState = getLinkExistingConfirmState(selected || null)
  const emptyState = getLinkExistingEmptyState({
    candidates: lists.candidates,
    suggested: lists.suggested,
    browse: lists.browse,
    filtered: lists.filtered,
    searchQuery,
  })
  const invoicesDupPath = venueId
    ? buildInvoicesPath(venueId, { matching: 'possible_duplicate' })
    : null
  const reconcileStyles = getGrActionToneStyles('green')

  const renderRow = invoice => {
    const id = getLinkExistingInvoiceId(invoice)
    const isSelected = String(selectedInvoiceId || '') === id
    const canReconcile = canLinkAndReconcileSelection(invoice)
    const matchingLabel = getInvoiceMatchingDisplayLabel(invoice)
    const isCreditNote = isCreditNoteDocument(invoice)
    const dateLabel = invoice.invoiceDate
      ? moment(invoice.invoiceDate).format('D MMM YYYY')
      : invoice.uploadedDate
      ? moment(invoice.uploadedDate).format('D MMM YYYY')
      : '—'

    return (
      <InvoiceRow
        key={id}
        type='button'
        $selected={isSelected}
        disabled={confirmLoading}
        onClick={() => onSelectInvoice?.(invoice)}
        aria-pressed={isSelected}
      >
        <RowMain>
          <RowTitle>
            {invoice.invoiceNumber
              ? `#${invoice.invoiceNumber}`
              : 'No invoice #'}
            {isCreditNote ? (
              <Tag color='purple' style={{ marginLeft: 8 }}>
                Credit note
              </Tag>
            ) : null}
            {canReconcile ? (
              <Tag color='green' style={{ marginLeft: 8 }}>
                Within tolerance
              </Tag>
            ) : null}
            {invoice.matchingStatus === 'possible_duplicate' ? (
              <Tag color='purple' style={{ marginLeft: 8 }}>
                Possible duplicate
              </Tag>
            ) : null}
          </RowTitle>
          <RowMeta>
            {formatInvoiceTotalDisplay(invoice.invoiceTotal)} · {dateLabel}
            {matchingLabel ? ` · ${matchingLabel}` : ''}
          </RowMeta>
          {invoice.fileName ? (
            <RowFile title={invoice.fileName}>{invoice.fileName}</RowFile>
          ) : null}
        </RowMain>
        {isSelected ? <SelectedMark>Selected</SelectedMark> : null}
      </InvoiceRow>
    )
  }

  return (
    <StyledModal
      title={
        confirmState.isCreditNote
          ? 'Apply credit note'
          : 'Link existing invoice'
      }
      visible={visible}
      onCancel={confirmLoading ? undefined : onCancel}
      maskClosable={!confirmLoading}
      footer={
        <FooterRow>
          <Button onClick={onBack} disabled={confirmLoading}>
            Back
          </Button>
          <Button onClick={onUploadNew} disabled={confirmLoading}>
            Upload new instead
          </Button>
          {confirmState.isCreditNote ? (
            <Tooltip title={GR_LINK_EXISTING_COPY.applyCreditOnlyTooltip}>
              <span>
                <Button
                  type='primary'
                  disabled={!confirmState.canLink || confirmLoading}
                  loading={confirmLoading}
                  onClick={() => selected && onLinkOnly?.(selected)}
                >
                  {CN_DOCUMENT_COPY.applyActionLabel}
                </Button>
              </span>
            </Tooltip>
          ) : (
            <>
              <Tooltip
                title={
                  confirmState.canLink
                    ? GR_LINK_EXISTING_COPY.linkOnlyTooltip
                    : 'Select an invoice first.'
                }
              >
                <span>
                  <Button
                    disabled={!confirmState.canLink || confirmLoading}
                    loading={confirmLoading}
                    onClick={() => selected && onLinkOnly?.(selected)}
                  >
                    Link only
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  confirmState.canReconcile
                    ? GR_LINK_EXISTING_COPY.linkAndReconcileTooltip
                    : confirmState.reconcileDisabledReason
                }
              >
                <span>
                  <ReconcileButton
                    type='default'
                    $toneStyles={reconcileStyles}
                    disabled={!confirmState.canReconcile || confirmLoading}
                    loading={confirmLoading}
                    onClick={() => selected && onLinkAndReconcile?.(selected)}
                  >
                    Link &amp; reconcile
                  </ReconcileButton>
                </span>
              </Tooltip>
            </>
          )}
        </FooterRow>
      }
      destroyOnClose
      width={560}
    >
      <Lead>
        Choose an open invoice or credit note for{' '}
        <strong>{supplierTitle || 'this supplier'}</strong>
        {po ? (
          <>
            {' '}
            to link to PO <strong>{po}</strong>
          </>
        ) : null}
        . Linking does not create a duplicate upload.
      </Lead>

      <Search
        allowClear
        placeholder='Search invoice #, date, or filename'
        value={searchQuery}
        onChange={event => setSearchQuery(event.target.value)}
        style={{ marginBottom: 16 }}
        disabled={confirmLoading}
      />

      {emptyState.kind === 'empty_library' ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={<span>{emptyState.message}</span>}
        >
          <Button type='primary' onClick={onUploadNew}>
            {GR_LINK_EXISTING_COPY.uploadNewOptionTitle}
          </Button>
        </Empty>
      ) : queryActive ? (
        <>
          <SectionLabel>Search results ({lists.filtered.length})</SectionLabel>
          {lists.filtered.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={emptyState.message}
            >
              <Button type='primary' onClick={onUploadNew}>
                {GR_LINK_EXISTING_COPY.uploadNewOptionTitle}
              </Button>
            </Empty>
          ) : (
            <List>{lists.filtered.map(renderRow)}</List>
          )}
        </>
      ) : (
        <>
          <SectionLabel>Suggested for this PO</SectionLabel>
          {lists.suggested.length === 0 ? (
            <EmptySuggestions>
              <Hint>{GR_LINK_EXISTING_COPY.emptySuggestions}</Hint>
              {emptyState.showUploadCta ? (
                <Button type='primary' onClick={onUploadNew}>
                  {GR_LINK_EXISTING_COPY.uploadNewOptionTitle}
                </Button>
              ) : null}
            </EmptySuggestions>
          ) : (
            <List>{lists.suggested.map(renderRow)}</List>
          )}

          <SectionLabel style={{ marginTop: 16 }}>
            Browse library ({lists.browse.length})
          </SectionLabel>
          {lists.browse.length === 0 ? (
            <Hint>{GR_LINK_EXISTING_COPY.emptyBrowse}</Hint>
          ) : (
            <List>{lists.browse.map(renderRow)}</List>
          )}
        </>
      )}

      {selected ? (
        <SelectedBanner $warning={confirmState.isPossibleDuplicate}>
          <div>
            <strong>
              Selected{' '}
              {selected.invoiceNumber
                ? `#${selected.invoiceNumber}`
                : 'invoice'}
            </strong>
            {confirmState.isPossibleDuplicate ? (
              <span>
                This looks like a possible duplicate. Review on Invoices before
                reconciling.
                {invoicesDupPath ? (
                  <>
                    {' '}
                    <Link to={invoicesDupPath} onClick={onCancel}>
                      Open possible duplicates
                    </Link>
                  </>
                ) : null}
              </span>
            ) : confirmState.canReconcile ? (
              <span>
                Totals look within tolerance — Link &amp; reconcile finishes
                reconciliation; Link only soft-links (Invoice attached, not
                Reconciled). Receipted is still only delivery truth.
              </span>
            ) : (
              <span>{confirmState.reconcileDisabledReason}</span>
            )}
          </div>
        </SelectedBanner>
      ) : null}
    </StyledModal>
  )
}

const StyledModal = styled(Modal)`
  .ant-modal-body {
    padding-top: 12px;
  }
`

const Lead = styled.p`
  margin: 0 0 14px;
  color: #595959;
  line-height: 1.5;
  font-size: 14px;
`

const SectionLabel = styled.div`
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #8c8c8c;
`

const Hint = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  color: #8c8c8c;
  line-height: 1.45;
`

const EmptySuggestions = styled.div`
  margin-bottom: 8px;
`

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 280px;
  overflow: auto;
`

const InvoiceRow = styled.button`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid ${props => (props.$selected ? '#52c41a' : '#e8e8e8')};
  background: ${props => (props.$selected ? '#f6ffed' : '#fff')};
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: ${props => (props.$selected ? '#389e0d' : '#b7eb8f')};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`

const RowMain = styled.div`
  min-width: 0;
  flex: 1;
`

const RowTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-weight: 600;
  color: #262626;
  font-size: 14px;
`

const RowMeta = styled.div`
  margin-top: 4px;
  font-size: 13px;
  color: #595959;
`

const RowFile = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: #8c8c8c;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SelectedMark = styled.span`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #389e0d;
`

const SelectedBanner = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 8px;
  background: ${props => (props.$warning ? '#f9f0ff' : '#f6ffed')};
  border: 1px solid ${props => (props.$warning ? '#d3adf7' : '#b7eb8f')};
  font-size: 13px;
  line-height: 1.45;
  color: #595959;

  strong {
    display: block;
    color: ${props => (props.$warning ? '#531dab' : '#135200')};
    margin-bottom: 2px;
  }
`

const FooterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
`

const ReconcileButton = styled(stripTransientProps(Button))`
  && {
    border-color: ${props => props.$toneStyles?.border || '#52c41a'};
    background: ${props => props.$toneStyles?.background || '#f6ffed'};
    color: ${props => props.$toneStyles?.color || '#135200'};
  }

  &&:hover:not(:disabled),
  &&:focus:not(:disabled) {
    border-color: ${props => props.$toneStyles?.activeBorder || '#389e0d'};
    background: ${props => props.$toneStyles?.activeBackground || '#d9f7be'};
    color: ${props => props.$toneStyles?.color || '#135200'};
  }
`

export default GrLinkExistingInvoiceModal
